import React, { useEffect, useState } from "react";
import axios from "axios";
import ReactAnimatedWeather from "react-animated-weather";
import Forecast from "./Forecast";

export default function Weather() {
  const [city, setCity] = useState("");
  const [weatherData, setWeatherData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [error, setError] = useState("");
  const [unit, setUnit] = useState("C");
  const [showForecast, setShowForecast] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getCurrentCity();
  }, []);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
  }, [darkMode]);

  function updateCity(event) {
    setCity(event.target.value);
  }

  function handleResponse(response) {
    if (response.data.condition) {
      setWeatherData(response.data);
      setCity(response.data.city);
      setError("");
      setLoading(false);
    } else {
      setWeatherData(null);
      setForecastData(null);
      setError("Sorry, we couldn't find that city. Please try again.");
      setLoading(false);
    }
  }

  function handleForecastResponse(response) {
    if (response.data.daily) {
      setForecastData(response.data.daily.slice(0, 5));
    } else {
      setForecastData(null);
    }
  }

  function handleError() {
    setWeatherData(null);
    setForecastData(null);
    setError("Sorry, we couldn't find that city. Please try again.");
    setLoading(false);
  }

  function handleForecastError() {
    setForecastData(null);
  }

  function search(event) {
    event.preventDefault();

    const searchedCity = city.trim();

    if (!searchedCity) {
      setWeatherData(null);
      setForecastData(null);
      setError("Please enter a city.");
      return;
    }

    setLoading(true);
    setError("");
    setShowForecast(false);

    const apiKey = process.env.REACT_APP_SHECODES_API_KEY;

    const currentApiUrl = `https://api.shecodes.io/weather/v1/current?query=${searchedCity}&key=${apiKey}&units=metric`;

    const forecastApiUrl = `https://api.shecodes.io/weather/v1/forecast?query=${searchedCity}&key=${apiKey}&units=metric`;

    axios
      .get(currentApiUrl)
      .then(handleResponse)
      .catch(handleError);

    axios
      .get(forecastApiUrl)
      .then(handleForecastResponse)
      .catch(handleForecastError);
  }

  function getCurrentCity() {
    if (!navigator.geolocation) {
      setError("Location services are not supported by your browser.");
      return;
    }

    setLoading(true);
    setError("");
    setShowForecast(false);

    navigator.geolocation.getCurrentPosition(
      handleCurrentPosition,
      handleLocationError
    );
  }

  function handleCurrentPosition(position) {
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    const apiKey = process.env.REACT_APP_SHECODES_API_KEY;

    const currentApiUrl = `https://api.shecodes.io/weather/v1/current?lat=${latitude}&lon=${longitude}&key=${apiKey}&units=metric`;

    const forecastApiUrl = `https://api.shecodes.io/weather/v1/forecast?lat=${latitude}&lon=${longitude}&key=${apiKey}&units=metric`;

    axios
      .get(currentApiUrl)
      .then(handleResponse)
      .catch(handleError);

    axios
      .get(forecastApiUrl)
      .then(handleForecastResponse)
      .catch(handleForecastError);
  }

  function handleLocationError() {
    setLoading(false);
    setError(
      "We couldn't access your current location. Please search for a city instead."
    );
  }

  function getWeatherIcon(description) {
    const weather = description.toLowerCase();

    if (weather.includes("clear")) {
      return "CLEAR_DAY";
    }

    if (
      weather.includes("rain") ||
      weather.includes("drizzle") ||
      weather.includes("thunderstorm")
    ) {
      return "RAIN";
    }

    if (weather.includes("snow")) {
      return "SNOW";
    }

    if (
      weather.includes("fog") ||
      weather.includes("mist") ||
      weather.includes("haze")
    ) {
      return "FOG";
    }

    if (weather.includes("cloud")) {
      return "CLOUDY";
    }

    return "PARTLY_CLOUDY_DAY";
  }

  function formatDate(timestamp) {
    const date = new Date(timestamp * 1000);

    return date.toLocaleDateString("en-AU", {
      weekday: "long",
      day: "numeric",
      month: "short",
    });
  }

  function convertTemperature(celsius) {
    if (unit === "F") {
      return Math.round((celsius * 9) / 5 + 32);
    }

    return Math.round(celsius);
  }

  return (
    <div className={`weather-app ${darkMode ? "dark" : ""}`}>
      <div className="top-controls">
        <button
          type="button"
          className="dark-mode-button"
          onClick={() => setDarkMode(!darkMode)}
        >
          {darkMode ? "☀️ Light Mode" : "🌙 Dark Mode"}
        </button>
      </div>

      <h1>Weather App</h1>

      <form onSubmit={search} className="search-form">
        <input
          type="search"
          placeholder="Enter a city..."
          value={city}
          onChange={updateCity}
          className="search-input"
        />

        <input
          type="submit"
          value="Search"
          className="search-button"
        />

        <button
          type="button"
          className="current-city-button"
          onClick={getCurrentCity}
        >
          Current City
        </button>
      </form>

      {loading && <p className="loading-message">Loading weather...</p>}

      {error && <p className="error-message">{error}</p>}

      {weatherData && weatherData.condition && (
        <>
          <div className="current-weather">
            <div className="current-weather-info">
              <h2>{weatherData.city}</h2>

              <p className="current-date">
                Today · {formatDate(weatherData.time)}
              </p>

              <p className="weather-description">
                {weatherData.condition.description}
              </p>

              <p className="weather-meta">
                💨 Wind:{" "}
                <strong>
                  {Math.round(weatherData.wind.speed * 3.6)} km/h
                </strong>

                <span className="weather-divider">•</span>

                💧 Humidity:{" "}
                <strong>
                  {weatherData.temperature.humidity}%
                </strong>
              </p>
            </div>

            <div className="current-weather-temperature">
              <ReactAnimatedWeather
                icon={getWeatherIcon(
                  weatherData.condition.description
                )}
                color={darkMode ? "#b9a8ff" : "#7257d9"}
                size={90}
                animate={true}
              />

              <span className="temperature-value">
                {convertTemperature(
                  weatherData.temperature.current
                )}

                <span className="temperature-unit">
                  °{unit}
                </span>
              </span>
            </div>
          </div>

          <div className="weather-actions">
            <div className="unit-toggle">
              <button
                type="button"
                className={unit === "C" ? "unit-active" : ""}
                onClick={() => setUnit("C")}
              >
                °C
              </button>

              <button
                type="button"
                className={unit === "F" ? "unit-active" : ""}
                onClick={() => setUnit("F")}
              >
                °F
              </button>
            </div>

            <button
              type="button"
              className="forecast-toggle"
              onClick={() => setShowForecast(!showForecast)}
              disabled={!forecastData}
            >
              {showForecast
                ? "Hide 5-Day Forecast"
                : "Show 5-Day Forecast"}
            </button>
          </div>
        </>
      )}

      {showForecast && forecastData && (
        <Forecast
          data={forecastData}
          unit={unit}
          darkMode={darkMode}
        />
      )}

      <footer>
        <p>
          Coded by{" "}
          <a
            href="https://github.com/rzmeridores"
            target="_blank"
            rel="noreferrer"
          >
            Ranamae Meridores
          </a>
          , and{" "}
          <a
            href="https://github.com/rzmeridores/react-ajax-weather-search-engine"
            target="_blank"
            rel="noreferrer"
          >
            open-sourced on GitHub
          </a>
          .
        </p>
      </footer>
    </div>
  );
}